import React, { useState } from 'react';
import {
  Server, Camera, Gauge, LifeBuoy, CheckCircle,
  Activity, AlertTriangle, HardDrive, Cpu, Clock,
} from 'lucide-react';

const VM_BLUEPRINTS = [
  { id: 'vm-automated-provisioning', name: 'Automated VM Provisioning', description: 'Self-service VM from template: CPU/RAM/disk, networking, AD account, DNS, CMDB.', icon: Server, difficulty: 'intermediate', duration: '~8 min', category: 'Lifecycle', rating: 4.9, usageCount: 3241 },
  { id: 'vm-snapshot-lifecycle', name: 'Snapshot Lifecycle Management', description: 'Create pre-change snapshots, enforce retention, detect and clean orphaned snapshots.', icon: Camera, difficulty: 'beginner', duration: '~2 min', category: 'Snapshots', rating: 4.8, usageCount: 2187 },
  { id: 'vm-capacity-rightsizing', name: 'Capacity & Rightsizing', description: 'Identify over/under-provisioned VMs with vROps data, recommend or auto-adjust resources.', icon: Gauge, difficulty: 'advanced', duration: '~20 min', category: 'Capacity', rating: 4.7, usageCount: 987 },
  { id: 'vm-dr-orchestration', name: 'DR Orchestration & Failover', description: 'Full DC failover: validate HA, tiered boot order, service validation, DNS failover.', icon: LifeBuoy, difficulty: 'advanced', duration: '~45 min', category: 'DR', rating: 4.9, usageCount: 234 },
  { id: 'vm-ha-compliance-check', name: 'HA & Compliance Check', description: 'Verify HA protection, VMware Tools currency, and configuration drift across all VMs.', icon: CheckCircle, difficulty: 'intermediate', duration: '~10 min', category: 'Compliance', rating: 4.6, usageCount: 1432 },
];

const STATS = [
  { label: 'Total VMs', value: '1,284', icon: Server, color: 'text-violet-400', bg: 'bg-violet-500/10' },
  { label: 'CPU Usage Avg', value: '42%', icon: Cpu, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { label: 'HA Issues', value: '3', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { label: 'Provisioned Today', value: '17', icon: Activity, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'bg-emerald-500/20 text-emerald-300',
  intermediate: 'bg-blue-500/20 text-blue-300',
  advanced: 'bg-purple-500/20 text-purple-300',
};

export function VirtualizationPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Lifecycle', 'Snapshots', 'Capacity', 'DR', 'Compliance'];
  const filtered = activeCategory === 'All' ? VM_BLUEPRINTS : VM_BLUEPRINTS.filter((b) => b.category === activeCategory);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Server className="w-5 h-5 text-violet-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">VMware Virtualization</h1>
          </div>
          <p className="text-gray-500">VM lifecycle, snapshots, DR orchestration, and capacity management</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-emerald-300 font-medium">vCenter Connected</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Cluster Overview */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4">Cluster Overview</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { name: 'Cluster-PROD-01', vms: 412, cpu: '38%', mem: '71%', status: 'healthy' },
            { name: 'Cluster-DEV-01', vms: 234, cpu: '22%', mem: '45%', status: 'healthy' },
            { name: 'Cluster-DR-01', vms: 638, cpu: '12%', mem: '28%', status: 'warning' },
          ].map((cluster) => (
            <div key={cluster.name} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white">{cluster.name}</span>
                <span className={`w-2 h-2 rounded-full ${cluster.status === 'healthy' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              </div>
              <div className="text-xs text-gray-400 mb-3">{cluster.vms} VMs</div>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>CPU</span><span>{cluster.cpu}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: cluster.cpu }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Memory</span><span>{cluster.mem}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full">
                    <div className={`h-full rounded-full ${parseInt(cluster.mem) > 70 ? 'bg-amber-500' : 'bg-violet-500'}`} style={{ width: cluster.mem }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Automation Blueprints</h2>
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCategory === cat ? 'bg-orbayx-600/30 text-orbayx-700 border border-orbayx-500/30' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((bp) => {
            const Icon = bp.icon;
            return (
              <div key={bp.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orbayx-500/30 transition-all group cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-violet-400" />
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_COLORS[bp.difficulty]}`}>{bp.difficulty}</span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-orbayx-700 transition-colors">{bp.name}</h3>
                <p className="text-xs text-gray-400 mb-3 line-clamp-2">{bp.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{bp.duration}</span>
                  <span>⭐ {bp.rating} · {bp.usageCount.toLocaleString()} runs</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
